//========================================================================================
/*
    Config params
      withoutLoader     [bool]    - don't show preloader
      disableAuthToken  [bool]    - disable use auth token
 
 */
//========================================================================================



import axios from 'axios';
import config from 'config';
import store from '../store/createStore';
import i18n from 'src/i18n';
import { uiShowPreLoader, uiHidePreLoader } from '../store/ui-reducer';
import { LANGUAGES } from './languages';

import decoded from 'jwt-decode';
import { updateAccessTokenTime, refreshTokenSuccess, refreshTokenExpired, logout }
  from '../store/auth/actions';
import { setUserValidationStatus } from 'src/views/patient/Profile/reducer';
import { PATIENT_VALIDATED } from 'src/services/constants';

export const sendMessageToApp = data => {
  try {
    window.postMessage(JSON.stringify(data), window.origin);
  } catch (e) {
    console.log('sendMessageToApp Err ', e);
  }
};

const readAccessToken = () => {
  const localToken = JSON.parse(localStorage.getItem('token'));
  const st = store.getState();
  return (localToken && localToken.access_token) || (st && st.auth && st.auth.token);
};

//──── Auth ──────────────────────────────────────────────────────────────────────────────
const http = axios.create({
  baseURL: config.api
});

http.interceptors.request.use(
  config => {
    if (!config.withoutLoader) {
      store.dispatch(uiShowPreLoader());
    }

    config.headers['Accept-Language'] = LANGUAGES[i18n.language];
    config.headers['Cache-Control'] = 'no-cache';
    config.headers['Pragma'] = 'no-cache';

    let originalRequest = config;
    let accessToken = readAccessToken();

    if (accessToken && !config.disableAuthToken) {
      originalRequest.headers['Authorization'] = 'Bearer ' + accessToken;
      return Promise.resolve(originalRequest);
    }
    return config;
  },
  err => {
    store.dispatch(uiHidePreLoader());
    return Promise.reject(err);
  }
);

let isRefreshing = false;
let subscribers = [];

function subscribeTokenRefresh(cb) {
  subscribers.push(cb);
}

function onRrefreshed(token) {
  subscribers.map(cb => cb(token));
}

http.interceptors.response.use(
  response => {
    if (!response.config.withoutLoader) {
      store.dispatch(uiHidePreLoader());
    }
    return response;
  },
  err => {
    const { config } = err;
    const status = err && err.response && err.response.status;
    const state = store.getState();
    const { auth } = state;
    const originalRequest = config;

    if (status === 401 && !auth.isTokenExpired) {
      if (!isRefreshing) {
        isRefreshing = true;
        refreshAccessToken(auth.refreshToken)
          .then(respaonse => {
            const { data } = respaonse;
            isRefreshing = false;
            onRrefreshed(data.access_token);
            store.dispatch(updateAccessTokenTime(decoded(data.access_token) || {}));
            store.dispatch(refreshTokenSuccess(data));
            store.dispatch(uiHidePreLoader());
            subscribers = [];
          })
          .catch(() => {
            tokenWasExpired();
          });
      }
      const requestSubscribers = new Promise(resolve => {
        subscribeTokenRefresh(token => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          resolve(axios(originalRequest));
        });
      });
      return requestSubscribers;
    }
    store.dispatch(uiHidePreLoader());
    return Promise.reject(err);
  }
);

//──── Booking ───────────────────────────────────────────────────────────────────────────

const bookingApi = axios.create({
  baseURL: config.bookingApi
});

bookingApi.interceptors.request.use(
  config => {
    if (!config.withoutLoader) {
      store.dispatch(uiShowPreLoader());
    }

    config.headers['Accept-Language'] = LANGUAGES[i18n.language];
    config.headers['Cache-Control'] = 'no-cache';
    config.headers['Pragma'] = 'no-cache';

    let originalRequest = config;
    let accessToken = readAccessToken();

    if (accessToken && !config.disableAuthToken) {
      originalRequest.headers['Authorization'] = 'Bearer ' + accessToken;
      return Promise.resolve(originalRequest);
    }
    return config;
  },
  err => {
    store.dispatch(uiHidePreLoader());
    return Promise.reject(err);
  }
);

bookingApi.interceptors.response.use(
  config => {
    // check user validation or need manually refresh
    if (config.headers.is_validated || config.config.repeatAfterValidation) {
      const originalRequest = config.config;
      if (!isRefreshing) {
        isRefreshing = true;
        const state = store.getState();
        const { auth } = state;
        refreshAccessToken(auth.refreshToken)
          .then(respaonse => {
            const { data } = respaonse;
            isRefreshing = false;
            onRrefreshed(data.access_token);
            store.dispatch(updateAccessTokenTime(decoded(data.access_token) || {}));
            store.dispatch(refreshTokenSuccess(data));
            store.dispatch(uiHidePreLoader());
            store.dispatch(setUserValidationStatus(PATIENT_VALIDATED));
            subscribers = [];
          })
          .catch(() => {
            tokenWasExpired();
          });
      }
      const requestSubscribers = new Promise(resolve => {
        subscribeTokenRefresh(token => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          resolve(axios(originalRequest));
        });
      });
      return requestSubscribers;
    }

    if (!config.config.withoutLoader) {
      store.dispatch(uiHidePreLoader());
    }
    return config;
  },
  err => {
    const { config } = err;
    const originalRequest = config;
    const state = store.getState();
    const status = err && err.response && err.response.status;
    const { auth } = state;

    if (status === 401 && !auth.isTokenExpired) {
      if (!isRefreshing) {
        isRefreshing = true;
        refreshAccessToken(auth.refreshToken)
          .then(respaonse => {
            const { data } = respaonse;
            isRefreshing = false;
            onRrefreshed(data.access_token);
            store.dispatch(updateAccessTokenTime(decoded(data.access_token) || {}));
            store.dispatch(refreshTokenSuccess(data));
            store.dispatch(uiHidePreLoader());
            subscribers = [];
          })
          .catch(() => {
            tokenWasExpired();
          });
      }
      const requestSubscribers = new Promise(resolve => {
        subscribeTokenRefresh(token => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          resolve(axios(originalRequest));
        });
      });
      return requestSubscribers;
    }
    store.dispatch(uiHidePreLoader());
    return Promise.reject(err);
  }
);

//──── Communication ──────────────────────────────────────────────────────────────────────────────

const communicateApi = axios.create({
  baseURL: config.communicationApi
});

communicateApi.interceptors.request.use(
  config => {
    if (!config.withoutLoader) {
      store.dispatch(uiShowPreLoader());
    }

    config.headers['Accept-Language'] = LANGUAGES[i18n.language];
    config.headers['Cache-Control'] = 'no-cache';
    config.headers['Pragma'] = 'no-cache';

    let originalRequest = config;
    let accessToken = readAccessToken();

    if (accessToken && !config.disableAuthToken) {
      originalRequest.headers['Authorization'] = 'Bearer ' + accessToken;
      return Promise.resolve(originalRequest);
    }
    return config;
  },
  err => {
    store.dispatch(uiHidePreLoader());
    return Promise.reject(err);
  }
);

communicateApi.interceptors.response.use(
  response => {
    if (!response.config.withoutLoader) {
      store.dispatch(uiHidePreLoader());
    }
    return response;
  },
  err => {
    const { config } = err;
    const status = err && err.response && err.response.status;
    const state = store.getState();
    const { auth } = state;
    const originalRequest = config;

    if (status === 401 && !auth.isTokenExpired) {
      if (!isRefreshing) {
        isRefreshing = true;
        refreshAccessToken(auth.refreshToken)
          .then(respaonse => {
            const { data } = respaonse;
            isRefreshing = false;
            onRrefreshed(data.access_token);
            store.dispatch(updateAccessTokenTime(decoded(data.access_token) || {}));
            store.dispatch(refreshTokenSuccess(data));
            store.dispatch(uiHidePreLoader());
            subscribers = [];
          })
          .catch(() => {
            tokenWasExpired();
          });
      }
      const requestSubscribers = new Promise(resolve => {
        subscribeTokenRefresh(token => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          resolve(axios(originalRequest));
        });
      });
      return requestSubscribers;
    }
    store.dispatch(uiHidePreLoader());
    return Promise.reject(err);
  }
);

//──── Payments ──────────────────────────────────────────────────────────────────────────

const paymentApi = axios.create({
  baseURL: config.paymentApi
});

paymentApi.interceptors.request.use(
  config => {
    if (!config.withoutLoader) {
      store.dispatch(uiShowPreLoader());
    }
    config.headers['Cache-Control'] = 'no-cache';
    config.headers['Pragma'] = 'no-cache';

    let originalRequest = config;
    let accessToken = readAccessToken();

    if (accessToken && !config.disableAuthToken) {
      originalRequest.headers['Authorization'] = 'Bearer ' + accessToken;
      return Promise.resolve(originalRequest);
    }
    return config;
  },
  err => {
    store.dispatch(uiHidePreLoader());
    return Promise.reject(err);
  }
);

paymentApi.interceptors.response.use(
  config => {
    store.dispatch(uiHidePreLoader());
    return config;
  },
  err => {
    const originalRequest = err.config;
    const state = store.getState();
    const { auth } = state;

    if (err.response && err.response.status === 401 && !auth.isTokenExpired) {
      if (!isRefreshing) {
        isRefreshing = true;
        const state = store.getState();
        const { auth } = state;
        refreshAccessToken(auth.refreshToken)
          .then(respaonse => {
            const { data } = respaonse;
            isRefreshing = false;
            onRrefreshed(data.access_token);
            store.dispatch(updateAccessTokenTime(decoded(data.access_token) || {}));
            store.dispatch(refreshTokenSuccess(data));
            store.dispatch(uiHidePreLoader());
            subscribers = [];
          })
          .catch(() => {
            tokenWasExpired();
          });
      }
      const requestSubscribers = new Promise(resolve => {
        subscribeTokenRefresh(token => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          resolve(axios(originalRequest));
        });
      });
      return requestSubscribers;
    }
    store.dispatch(uiHidePreLoader());
    return Promise.reject(err);
  }
);

// refresh token into interceptors
// used in services and etc.
// need added params withoutRedux: true

const refreshAccessToken = refreshToken => {
  const data = { refreshToken };
  return axios({
    method: 'post',
    url: `${config.api}auth/refreshtoken`,
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json; charset=utf-8'
    },
    data
  })
    .then(res => res)
    .catch(err => err);
};

const tokenWasExpired = () => {
  // send event to mobile app
  sendMessageToApp({ actionName: 'setSessionIsExpired' });

  const state = store.getState();
  const { auth } = state;

  store.dispatch(uiHidePreLoader());
  store.dispatch(logout(auth));
  store.dispatch(refreshTokenExpired());
};

export { http, bookingApi, communicateApi, paymentApi };
